import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { db, doc, updateDoc } from '../firebase/config';
import { useLocationLabel } from '../hooks/useLocationLabel';
import {
  Check, ArrowRight, ChevronLeft,
  MapPin, Navigation, Loader2, Sparkles, Search,
} from 'lucide-react';

const MAX_PREFERENCES = 5;
const MIN_PREFERENCES = 2;

// ---------- Nominatim Search Helper ----------
const searchAddress = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Skytone/1.0 (contact@skytoneby.co.za)'
        }
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Address search failed:', error);
    return [];
  }
};

const OnboardingClient = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const {
    currentLocation,
    requestLocation,
    loading: locationLoading,
  } = useLocation();

  const [step, setStep] = useState(1);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);

  const [coords, setCoords] = useState(currentLocation || null);
  const [geoError, setGeoError] = useState(null);

  // Address Search State
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimerRef = useRef(null);

  const locationLabel = useLocationLabel(coords);

  useEffect(() => {
    if (locationLabel && !addressQuery) {
      setAddressQuery(locationLabel);
    }
  }, [locationLabel, addressQuery]);

  const categories = [
    { id: 'hair',   name: 'Hair',   image: 'woman-hair.svg' },
    { id: 'nails',  name: 'Nails',  image: 'nail-polish.svg' },
    { id: 'lashes', name: 'Lashes', image: 'mascara.svg' },
    { id: 'skin',   name: 'Skin',   image: 'make-up.svg' },
    { id: 'spa',    name: 'Spa',    image: 'spa.svg' },
    // ADDED MAKEUP CATEGORY BELOW
    { id: 'makeup', name: 'Makeup', image: 'make-up.svg' }, 
  ];

  const togglePreference = (categoryId) => {
    if (preferences.includes(categoryId)) {
      setPreferences(preferences.filter((id) => id !== categoryId));
    } else if (preferences.length < MAX_PREFERENCES) {
      setPreferences([...preferences, categoryId]);
    }
  };

  // ─── Geolocation handlers ───
  const handleUseCurrentLocation = async () => {
    setGeoError(null);
    try {
      const result = await requestLocation();
      const resolved = result || currentLocation;
      if (resolved?.lat != null && resolved?.lng != null) {
        setCoords(resolved);
      } else {
        setGeoError('Could not read your location. Try searching instead.');
      }
    } catch (err) {
      console.error('Geolocation failed:', err);
      setGeoError('Location permission denied. You can search for your address instead.');
    }
  };

  const handleSkipLocation = () => {
    setCoords(null);
    setGeoError(null);
    setStep(3);
  };

  // ─── Completion ───
  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        preferences,
        coordinates: coords
          ? { ...coords, label: locationLabel || coords.label || null }
          : null,
        onboardingComplete: true,
        userType: 'client',
        role: 'client',
        updatedAt: new Date().toISOString(),
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;
  const canProceedStep1 = preferences.length >= MIN_PREFERENCES;
  const canProceedStep2 = !!coords;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 py-8 relative overflow-hidden bg-[#EFA9B6]"
    >
      {/* Subtle radial highlight for depth on solid pink */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 20%, rgba(255,255,255,0.35), rgba(232,139,156,0.55) 70%)',
        }}
      />

      {/* Glass card */}
      <div className="relative w-full max-w-md z-10">
        <div className="backdrop-blur-2xl bg-white/25 border border-white/40 rounded-[2rem] shadow-2xl overflow-hidden p-6 max-h-[92vh] overflow-y-auto">

          {/* Progress Steps */}
          <div className="flex gap-1 mb-6">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Back Button */}
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="mb-4 text-white/70 hover:text-white transition-colors flex items-center gap-1 text-sm touch-manipulation active:scale-95"
            >
              <ChevronLeft size={16} /> Back
            </button>
          )}

          {/* ============ STEP 1 — PREFERENCES ============ */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">
                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2 leading-tight">
                  What do you love?
                </h1>
                <p className="text-gray-500 mb-6 text-sm">
                  Pick {MIN_PREFERENCES}-{MAX_PREFERENCES} favorites — {preferences.length}/
                  {MAX_PREFERENCES} selected
                </p>

                {/* Grid changed slightly to accommodate 6 items cleanly */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                  {categories.map((cat) => {
                    const selected = preferences.includes(cat.id);
                    const atLimit =
                      preferences.length >= MAX_PREFERENCES && !selected;
                    return (
                      <div
                        key={cat.id}
                        onClick={() => togglePreference(cat.id)}
                        className={`relative p-3 sm:p-4 rounded-[2rem] border-2 transition-all flex flex-col items-center gap-2 sm:gap-3 cursor-pointer touch-manipulation active:scale-95 ${
                          selected
                            ? 'border-[#EFA9B6] bg-[#FFF5F6] shadow-sm'
                            : atLimit
                            ? 'border-gray-50 bg-white opacity-40 cursor-not-allowed'
                            : 'border-gray-50 bg-white hover:border-[#FBE1E8]'
                        }`}
                      >
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className={`w-12 h-12 sm:w-14 sm:h-14 object-contain transition-opacity duration-200 ${
                            selected ? 'opacity-100' : 'opacity-40'
                          }`}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span
                          className={`text-xs sm:text-sm font-bold text-center ${
                            selected ? 'text-gray-900' : 'text-gray-500'
                          }`}
                        >
                          {cat.name}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => canProceedStep1 && setStep(2)}
                  disabled={!canProceedStep1}
                  className={`w-full py-4 rounded-full font-bold text-sm transition-transform active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 ${
                    canProceedStep1
                      ? 'bg-[#010101] text-white shadow-xl hover:bg-black/90'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue <ArrowRight size={18} />
                </button>
                {preferences.length < MIN_PREFERENCES && (
                  <p className="text-center text-xs text-gray-400 mt-3">
                    Select {MIN_PREFERENCES - preferences.length} more{' '}
                    {preferences.length < MIN_PREFERENCES ? 'category' : 'categories'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ============ STEP 2 — LOCATION ============ */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">
                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2 leading-tight">
                  Where are you?
                </h1>
                <p className="text-gray-500 mb-6 text-sm">
                  We'll show you pros and services closest to you.
                </p>

                {/* Pin illustration */}
                <div className="bg-[#FCF8F9] rounded-3xl p-8 mb-6 flex flex-col items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#EFA9B6]/20 flex items-center justify-center mb-3">
                    <div className="w-14 h-14 rounded-full bg-[#EFA9B6] flex items-center justify-center shadow-lg">
                      <MapPin size={26} className="text-white" />
                    </div>
                  </div>
                  {coords ? (
                    <div className="text-center">
                      <p className="text-base font-bold text-gray-900">
                        {locationLabel || 'Location set'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {coords.lat.toFixed(3)}, {coords.lng.toFixed(3)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 text-center">
                      Your location helps us rank pros by distance
                    </p>
                  )}
                </div>

                {geoError && (
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-3 mb-4">
                    <p className="text-xs text-red-600">{geoError}</p>
                  </div>
                )}

                {/* Address Search Autocomplete */}
                <div className="relative mb-4">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5 ml-1">
                    Search your city or address
                  </label>
                  <div className="relative">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={addressQuery}
                      onChange={(e) => {
                        setAddressQuery(e.target.value);
                        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                        
                        if (e.target.value.length >= 3) {
                          setSearching(true);
                          setShowSuggestions(true);
                          searchTimerRef.current = setTimeout(async () => {
                            const results = await searchAddress(e.target.value);
                            setSuggestions(results);
                            setSearching(false);
                          }, 600);
                        } else {
                          setSuggestions([]);
                          setShowSuggestions(false);
                          setSearching(false);
                        }
                      }}
                      onFocus={() => {
                        if (suggestions.length > 0) setShowSuggestions(true);
                      }}
                      placeholder="e.g. Sandton, Johannesburg"
                      className="w-full bg-[#FCF8F9] border-2 border-transparent rounded-2xl pl-11 pr-10 py-3.5 text-sm focus:border-[#EFA9B6] outline-none transition-colors"
                    />
                    {searching && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>

                  {/* Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {suggestions.map((place) => (
                        <button
                          key={place.place_id}
                          type="button"
                          onClick={() => {
                            setAddressQuery(place.display_name);
                            setCoords({
                              lat: parseFloat(place.lat),
                              lng: parseFloat(place.lon),
                              label: place.display_name
                            });
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-[#FCF8F9] border-b border-gray-50 last:border-0 text-sm text-gray-700"
                        >
                          {place.display_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-center text-xs text-gray-400 mb-4 font-medium">
                  — or —
                </div>

                {/* GPS Button */}
                <button
                  onClick={handleUseCurrentLocation}
                  disabled={locationLoading}
                  className="w-full py-4 rounded-full font-bold text-sm bg-[#EFA9B6] text-white shadow-lg hover:bg-[#EFA9B6]/90 active:scale-[0.98] touch-manipulation transition-all flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
                >
                  {locationLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Getting location...
                    </>
                  ) : (
                    <>
                      <Navigation size={18} /> Use my current location
                    </>
                  )}
                </button>

                {/* Continue / Skip */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 rounded-full font-bold text-sm bg-white text-gray-600 border-2 border-gray-100 hover:border-gray-200 transition-all touch-manipulation active:scale-95"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => canProceedStep2 && setStep(3)}
                    disabled={!canProceedStep2}
                    className={`flex-1 py-4 rounded-full font-bold text-sm transition-transform active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 ${
                      canProceedStep2
                        ? 'bg-[#010101] text-white shadow-xl hover:bg-black/90'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Continue <ArrowRight size={18} />
                  </button>
                </div>
                <button
                  onClick={handleSkipLocation}
                  className="w-full mt-3 text-xs text-white/70 hover:text-white transition-colors touch-manipulation p-2"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ============ STEP 3 — CONFIRMATION ============ */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg text-center">
                <div className="w-24 h-24 bg-[#FCF8F9] rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-16 h-16 bg-[#EFA9B6] rounded-full flex items-center justify-center">
                    <Check size={32} className="text-white" />
                  </div>
                </div>
                <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                  You're all set!
                </h1>
                <p className="text-gray-400 mb-8 text-sm">
                  Get ready to discover amazing beauty services near you
                </p>

                <div className="bg-[#FCF8F9] rounded-2xl p-4 mb-4 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-[#EFA9B6]" />
                    <span className="text-sm font-bold text-gray-700">Your Preferences</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preferences.map((id) => {
                      const cat = categories.find((c) => c.id === id);
                      return (
                        <span
                          key={id}
                          className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-600 border border-gray-100"
                        >
                          {cat?.name}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-[#FCF8F9] rounded-2xl p-4 mb-8 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={16} className="text-[#EFA9B6]" />
                    <span className="text-sm font-bold text-gray-700">Location</span>
                  </div>
                  {coords ? (
                    <div>
                      <p className="text-sm text-gray-900 font-medium">
                        {locationLabel || 'Location set'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {coords.lat.toFixed(3)}, {coords.lng.toFixed(3)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">
                      Not set — you can enable it later from your profile
                    </p>
                  )}
                </div>

                <button
                  onClick={completeOnboarding}
                  disabled={loading}
                  className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm shadow-xl hover:bg-black/90 transition-transform active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Setting up...
                    </div>
                  ) : (
                    <>
                      Start Exploring <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out forwards; }
      `,
        }}
      />
    </div>
  );
};

export default OnboardingClient;